import { ComputeFacetsResponse, VertesiaClient } from "@vertesia/client"

import { FacetResponse, PropertyFacet } from "@/models/facets"
import TypeRegistry from "@/plugins/session/TypeRegistry"


interface PropertyObj {
    [key: string]: any
}

export function transformPropertiesFacets(facets: any | undefined, properties: PropertyObj[]) {
    if (!facets || !properties) {
        return []
    }

    const propertyFacets: PropertyFacet[] = properties
        .map(facet => ({
            name: facet.name,
            field: facet.field,
            values: facets[facet.name] || [],
            match: facet.match || false
        }))

    return propertyFacets
}

export function transformProjectPropertiesFacetsWithFilter(facets: any | undefined, properties: PropertyObj[]) {
    if (!facets || !properties) {
        return []
    }

    const propertyFacets: PropertyFacet[] = properties
        .filter(facet => facet.field.startsWith("properties."))
        .map(facet => {
            const values = facets[facet.field] || []
            const countMap = new Map<string, number>()
            
            values.forEach((value: { _id: string, count: number }) => {
                if (value._id && value._id.includes(',')) {
                    const ids = value._id.split(',').map(id => id.trim())
                    ids.forEach(id => {
                        countMap.set(id, (countMap.get(id) || 0) + value.count)
                    })
                } else {
                    countMap.set(value._id, (countMap.get(value._id) || 0) + value.count)
                }
            })

            const aggregatedValues = Array.from(countMap.entries())
                .map(([_id, count]) => ({ _id, count }))
                .sort((a, b) => b.count - a.count)

            return {
                name: facet.name,
                field: facet.field,
                values: aggregatedValues,
                match: facet.match || false
            }
        })

    return propertyFacets
}

export function transformPropertiesFacetsWithFilter(facets: any | undefined, properties: PropertyObj[]) {
    if (!facets || !properties) {
        return []
    }
    const propertyFacets: PropertyFacet[] = properties
        .filter(facet => facet.field.startsWith("properties."))
        .map(facet => ({
            name: facet.name,
            field: facet.field,
            values: facets[facet.field] || [],
            match: facet.match || false
        }))

    return propertyFacets
}

export function transformFacetStatusResponses(facets: FacetResponse[] | undefined) {
    if (!facets?.length) {
        return []
    }

    const options = facets.map((facet) => {
        const name = facet._id || "Unknown"
        return {
            _id: facet._id,
            name,
            count: facet.count,
        }
    })
    options.sort((a, b) => a.name.localeCompare(b.name))
    return options
}

export function transformFacetTypeResponses(facets: FacetResponse[] | undefined, typeRegistry: TypeRegistry | undefined) {
    if (!facets?.length) {
        return []
    }

    if (!typeRegistry) {
        return facets
    }

    const options = facets.map((facet) => {
        let name
        if (facet._id == null) {
            facet._id = "Document"
            name = "Document"
        } else {
            name = typeRegistry.getTypeName(facet._id)
            if (!name) {
                name = facet._id
            }
        }
        return {
            _id: facet._id,
            name,
            count: facet.count
        }
    })
    options.sort((a, b) => a.name.localeCompare(b.name))
    return options
}

export function transformUser(user: string, client: VertesiaClient, callback: (result: string) => void): void {
    const [type, id] = user ? user.split(":") : []
    if (!id) {
        callback("Unknown User")
        return
    }

    if (type === "user") {
        client.users.retrieve(id)
            .then(user => {
                callback(user?.name || "Unknown User")
            })
            .catch(() => {
                callback("Unknown User")
            })
    } else if (type === "system") {
        callback("System")
    } else if (type === "service_account") {
        callback(`Service Account (${id})`)
    } else {
        callback("Unknown User")
    }
}

export function getOptionCount(filter: string, options: ComputeFacetsResponse): number {
    const filteredOptions: FacetResponse[] = (options as any)[filter] || []
    return Array.isArray(filteredOptions) ? filteredOptions.length : Object.keys(filteredOptions).length
}


export function mergeAndCombineCounts(arr1: { _id: string, count: number }[] = [], arr2: { _id: string, count: number }[] = []) {
    const countMap = new Map<string, number>()

    arr1.forEach(item => {
        countMap.set(item._id, (countMap.get(item._id) || 0) + item.count);
    })

    arr2.forEach(item => {
        countMap.set(item._id, (countMap.get(item._id) || 0) + item.count);
    })

    return Array.from(countMap.entries())
        .map(([_id, count]) => ({ _id, count }))
        .sort((a, b) => b.count - a.count)
}